<template>
  <v-container fluid class="pa-4">
    <v-row class="mb-6">
      <v-col cols="12">
        <div class="d-flex align-center mb-2">
          <v-avatar size="56" class="mr-4 primary">
            <v-icon size="30" color="white">mdi-flask-outline</v-icon>
          </v-avatar>
          <div>
            <h1 class="display-1 mb-1 font-weight-bold">Finlay Lab</h1>
            <p class="subtitle-1 grey--text text--darken-1 mb-0">
              Preferred laboratory information, payment access and procedures cost table.
            </p>
          </div>
        </div>
      </v-col>
    </v-row>

    <v-alert
      v-if="accessChecked && !hasAccess"
      type="error"
      prominent
      border="left"
      colored-border
      class="mb-4"
    >
      You do not have permission to access this page.
    </v-alert>

    <template v-else>
      <v-card v-if="showFinlayContent" class="mb-4" elevation="2">
        <v-card-title class="text-h6">About Finlay Clinical Laboratory</v-card-title>
        <v-card-text>
          <p class="mb-4">
            Finlay Clinical Laboratory is now the preferred laboratory for
            Family Health Choice Plan. If your office already has an account
            with Finlay, you can continue using the existing account number.
            For questions, contact Finlay at <strong>305-643-2702</strong>.
          </p>

          <div class="d-flex flex-wrap ga-3">
            <v-btn color="primary" @click="openPaymentLink">
              <v-icon left>mdi-open-in-new</v-icon>
              Open Finlay Payment Platform
            </v-btn>
            <v-btn outlined color="primary" href="/finlay/payment-qr-and-link.pdf" target="_blank">
              <v-icon left>mdi-file-pdf-box</v-icon>
              Open Payment PDF
            </v-btn>
            <v-btn outlined color="primary" href="/finlay/finlay-lab-costs.xlsx" target="_blank">
              <v-icon left>mdi-microsoft-excel</v-icon>
              Download Cost Spreadsheet
            </v-btn>
          </div>
        </v-card-text>
      </v-card>

      <v-alert
        v-else-if="accessChecked"
        type="info"
        prominent
        border="left"
        colored-border
        class="mb-4"
      >
        Finlay Lab is available only for members in PHC or DPO plans.
      </v-alert>

      <v-row v-if="showFinlayContent">
        <v-col cols="12" md="5">
          <v-card elevation="2" class="mb-4">
            <v-card-title class="text-h6">Payment QR Code</v-card-title>
            <v-card-text class="text-center">
              <img
                src="/finlay/payment-qr-page.png"
                alt="Finlay payment QR code"
                class="finlay-qr"
              >
              <div class="caption mt-2 grey--text text--darken-1">
                Scan to access payment page directly.
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="7">
          <v-card elevation="2" class="mb-4">
            <v-card-title class="text-h6">Procedures Cost List</v-card-title>
            <v-card-text>
              <v-tabs
                v-if="canViewBothRateTables"
                v-model="activeTab"
                background-color="transparent"
                color="primary"
                show-arrows
                class="mb-3"
              >
                <v-tab key="phc">PHC Rates</v-tab>
                <v-tab key="dpo">DPO Rates</v-tab>
              </v-tabs>

              <v-chip v-else color="primary" text-color="white" class="mb-4">
                {{ detectedPlanType }} Rates
              </v-chip>

              <v-text-field
                v-model="search"
                dense
                outlined
                clearable
                class="mt-4"
                label="Search procedure, code or CPT"
                prepend-inner-icon="mdi-magnify"
              />

              <v-data-table
                :headers="headers"
                :items="filteredItems"
                :items-per-page="15"
                :footer-props="{ 'items-per-page-options': [15, 30, 50, -1] }"
                dense
              >
                <template v-slot:item.price="{ item }">
                  ${{ formatPrice(item.price) }}
                </template>
                <template v-slot:item.cptCode="{ item }">
                  {{ item.cptCode || '-' }}
                </template>
                <template v-slot:item.testCode="{ item }">
                  {{ item.testCode || '-' }}
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </template>
  </v-container>
</template>

<script>
import axios from 'axios';
import { finlayLabCosts } from '../data/finlayLabCosts';

const FINLAY_PAYMENT_LINK = 'https://link.clover.com/urlshortener/RkqXRt';

export default {
  name: 'FinlayLab',
  data() {
    return {
      accessChecked: false,
      hasAccess: false,
      userRoleLower: '',
      detectedPlanType: null,
      activeTab: 0,
      search: '',
      headers: [
        { text: 'Procedure', value: 'testName' },
        { text: 'Test Code', value: 'testCode' },
        { text: 'CPT Code', value: 'cptCode' },
        { text: 'Price', value: 'price' },
      ],
    };
  },
  computed: {
    canViewBothRateTables() {
      return ['manager', 'superuser', 'superadmin'].includes(this.userRoleLower);
    },
    showFinlayContent() {
      if (!this.accessChecked || !this.hasAccess) return false;
      if (this.canViewBothRateTables) return true;
      return this.detectedPlanType === 'PHC' || this.detectedPlanType === 'DPO';
    },
    activeItems() {
      if (this.canViewBothRateTables) {
        return this.activeTab === 0 ? finlayLabCosts.phcRates : finlayLabCosts.dpoRates;
      }
      if (this.detectedPlanType === 'PHC') return finlayLabCosts.phcRates;
      if (this.detectedPlanType === 'DPO') return finlayLabCosts.dpoRates;
      return [];
    },
    filteredItems() {
      const q = String(this.search || '').toLowerCase().trim();
      if (!q) return this.activeItems;

      const filtered = [];
      this.activeItems.forEach((item) => {
        const values = [item.testName, item.testCode, item.cptCode].filter(Boolean);
        if (values.some(value => String(value).toLowerCase().includes(q))) {
          filtered.push(item);
        }
      });
      return filtered;
    },
  },
  async mounted() {
    await this.checkAccess();
  },
  methods: {
    async checkAccess() {
      try {
        const response = await axios.get('/api/user');
        const user = response.data && response.data[0];
        const role = user && user.role ? String(user.role).toLowerCase() : '';
        this.userRoleLower = role;
        this.hasAccess = ['member', 'manager', 'superuser', 'superadmin'].includes(role);

        if (!this.hasAccess) {
          this.$router.push('/');
          return;
        }

        if (this.canViewBothRateTables) {
          this.detectedPlanType = 'PHC';
          return;
        }

        this.detectedPlanType = await this.detectPlanType(user);
        if (!this.detectedPlanType) {
          this.$router.push('/member-enrollment');
        }
      } catch (error) {
        this.$router.push('/login');
        return;
      } finally {
        this.accessChecked = true;
      }
    },
    openPaymentLink() {
      window.open(FINLAY_PAYMENT_LINK, '_blank', 'noopener');
    },
    formatPrice(value) {
      return Number(value || 0).toFixed(2);
    },
    getPlanTypeFromText(value) {
      const text = String(value || '').trim().toLowerCase();
      if (!text) return null;
      if (text.includes('dpo')) return 'DPO';
      if (text.includes('phc') || text.includes('prepaid health clinic')) return 'PHC';
      return null;
    },
    extractPlanTypeFromObject(payload) {
      if (!payload) return null;
      const candidateKeys = [
        'planName',
        'PLAN_NAME',
        'planType',
        'PLAN_TYPE',
        'product',
        'PRODUCT',
        'groupName',
        'GROUP_NAME',
      ];
      for (let i = 0; i < candidateKeys.length; i += 1) {
        const key = candidateKeys[i];
        const type = this.getPlanTypeFromText(payload[key]);
        if (type) return type;
      }
      return null;
    },
    getMemberId(user) {
      if (!user) return '';
      return user.memberId || user.memberID || user.MEMBER_ID || user.subscriberId || '';
    },
    async detectPlanType(user) {
      const direct = this.extractPlanTypeFromObject(user)
        || this.extractPlanTypeFromObject(user && user.enrollment);
      if (direct) return direct;

      const insurance = user && user.insurance && user.insurance[0];
      const memberId = this.getMemberId(user);
      if (!insurance) return null;

      if (memberId) {
        try {
          const res = await axios.get(`/api/enrollment/member/${insurance}/${memberId}`);
          const fromMember = this.extractPlanTypeFromObject(res.data)
            || this.extractPlanTypeFromObject(res.data && res.data.enrollment);
          if (fromMember) return fromMember;
        } catch (error) {
          // no-op: fallback below
        }
      }

      try {
        const res = await axios.get(`/api/enrollment/members/${insurance}?t=${Date.now()}`);
        const family = res.data && res.data[0];
        const subscriber = family && family.subscriber;
        return (
          this.extractPlanTypeFromObject(family)
          || this.extractPlanTypeFromObject(subscriber)
          || null
        );
      } catch (error) {
        return null;
      }
    },
  },
};
</script>

<style scoped>
.finlay-qr {
  width: 100%;
  max-width: 420px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
}
</style>
